/**
 * Couples — Wedding Book user management.
 *
 * The "users" of a Wedding Life Book are couples, and their commercial state
 * is the TIER of a book they own rather than a recurring subscription. A
 * couple may own more than one book at more than one tier, which is why the
 * tier control lives on a book row inside the detail dialog and not on the
 * couple.
 *
 * Server-side paging, filtering and sorting, like WeddingBooksPage — the grid
 * is a view over a query. Search is debounced so a typed word is one request.
 *
 * This page owns every mutation and every toast; the dialogs are presentation.
 * No dialog fires a request before its confirm button is pressed, which is the
 * property `__e2e__/mutations.journey.test.tsx` exists to defend.
 *
 * `?locked=locked` IS A DEEP LINK, not an internal detail. The "Couples locked
 * out now" tile on Members navigates straight here with that filter applied —
 * a number and the list behind it should be one click apart, not a number and
 * then a hunt through a select. The other filters stay local for now; only the
 * one with an entry point elsewhere in the product needs to survive a URL.
 */

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import KeyIcon from '@mui/icons-material/VpnKey';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridPaginationModel,
} from '@mui/x-data-grid';
import EmptyState from '../../common/EmptyState';
import useUrlParam from '../../common/useUrlParam';
import { WeddingTierChip } from '../WeddingChips';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  CoupleRow, CouplesResponse, CoupleDetail, CoupleBookRow, Tier,
} from '../weddingApi';
import { TIERS, TIER_META, centsToUsd, num, relativeTime, shortDate } from '../weddingFormat';
import CoupleDetailDialog from './CoupleDetailDialog';
import CoupleEditDialog from './CoupleEditDialog';
import PasswordResetDialog from './PasswordResetDialog';
import TierChangeDialog from './TierChangeDialog';

/** Mirrors COUPLE_SORT_COLUMNS in weddingAdmin.js. Anything else is ignored server-side. */
const SORTABLE = new Set(['created_at', 'last_login_at', 'email', 'books', 'revenue_cents', 'top_tier']);

/**
 * What `?locked=` may hold. 'all' is the DEFAULT rather than a member of this
 * list, so it is deleted from the URL instead of parked on it — and a stale
 * `?locked=maybe` falls back to 'all' and renders, rather than erroring on a
 * bookmark somebody saved.
 */
const LOCKED_PARAM_VALUES: readonly string[] = ['locked', 'unlocked'];

type Toast = { severity: 'success' | 'error' | 'warning' | 'info'; message: string } | null;

const WeddingCouplesPage: React.FC = () => {
  const [rows, setRows] = React.useState<CoupleRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [forbidden, setForbidden] = React.useState(false);

  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [verified, setVerified] = React.useState<'all' | 'verified' | 'unverified'>('all');
  const [locked, setLocked] = useUrlParam('locked', {
    defaultValue: 'all',
    allowed: LOCKED_PARAM_VALUES,
  });
  const [tier, setTier] = React.useState<Tier | 'all'>('all');

  const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detail, setDetail] = React.useState<CoupleDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);
  const [tierBook, setTierBook] = React.useState<CoupleBookRow | null>(null);
  const [toast, setToast] = React.useState<Toast>(null);

  // 350 ms, matching WeddingBooksPage.
  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sort = sortModel[0];
      const res: CouplesResponse = await weddingApi.getCouples({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        search: search || undefined,
        verified: verified === 'all' ? undefined : verified,
        locked: locked === 'all' ? undefined : (locked as 'locked' | 'unlocked'),
        tier: tier === 'all' ? undefined : tier,
        sortBy: sort && SORTABLE.has(sort.field) ? sort.field : undefined,
        sortOrder: sort && sort.sort ? sort.sort : undefined,
      });
      setRows(res.couples || []);
      setTotal(res.pagination ? res.pagination.total : 0);
    } catch (e) {
      const err = e as WeddingApiError;
      if (err.status === 403) setForbidden(true);
      setError(err.message || 'Could not load couples');
    } finally {
      setLoading(false);
    }
  }, [pagination, sortModel, search, verified, locked, tier]);

  React.useEffect(() => { load(); }, [load]);

  const openDetail = async (coupleId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      setDetail(await weddingApi.getCouple(coupleId));
    } catch (e) {
      setDetailError((e as WeddingApiError).message || 'Could not load the couple');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    try { setDetail(await weddingApi.getCouple(detail.couple.id)); } catch { /* toast already shown */ }
  };

  const doReset = async () => {
    if (!detail) return;
    try {
      const r = await weddingApi.resetCouplePassword(detail.couple.id);
      setToast({ severity: 'success', message: `Reset email sent to ${r.email_masked}.` });
      setResetOpen(false);
      refreshDetail();
    } catch (e) {
      const err = e as WeddingApiError;
      setToast({
        severity: err.code === 'RESET_RATE_LIMITED' ? 'warning' : 'error',
        message: err.message,
      });
      setResetOpen(false);
    }
  };

  const doResendVerification = async () => {
    if (!detail) return;
    try {
      const r = await weddingApi.resendCoupleVerification(detail.couple.id);
      setToast({ severity: 'success', message: `Verification email sent to ${r.email_masked}.` });
    } catch (e) {
      setToast({ severity: 'error', message: (e as WeddingApiError).message });
    }
  };

  const doUnlock = async () => {
    if (!detail) return;
    try {
      await weddingApi.unlockCouple(detail.couple.id);
      setToast({ severity: 'success', message: 'Account unlocked.' });
      refreshDetail();
      load();
    } catch (e) {
      setToast({ severity: 'error', message: (e as WeddingApiError).message });
    }
  };

  const columns: GridColDef<CoupleRow>[] = [
    {
      field: 'partner1_name',
      headerName: 'Couple',
      flex: 1.6,
      minWidth: 240,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {p.row.partner1_name} &amp; {p.row.partner2_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">{p.row.email}</Typography>
        </Box>
      ),
    },
    {
      field: 'email_verified',
      headerName: 'Verified',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Chip
          size="small"
          variant="outlined"
          label={p.row.email_verified ? 'Yes' : 'No'}
          color={p.row.email_verified ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'books',
      headerName: 'Books',
      width: 130,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">{num(p.row.books)}</Typography>
          {p.row.top_tier && <WeddingTierChip tier={p.row.top_tier} />}
        </Stack>
      ),
    },
    {
      field: 'revenue_cents',
      headerName: 'Revenue',
      width: 120,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Typography variant="body2">{centsToUsd(p.row.revenue_cents)}</Typography>
      ),
    },
    {
      field: 'last_login_at',
      headerName: 'Last sign-in',
      width: 140,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Tooltip title={p.row.last_login_at ? shortDate(p.row.last_login_at) : ''}>
          <Typography variant="body2" color="text.secondary">
            {p.row.last_login_at ? relativeTime(p.row.last_login_at) : 'never'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'locked_out',
      headerName: 'Security',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Stack direction="row" spacing={0.5}>
          {p.row.locked_out && <Chip size="small" color="error" variant="outlined" label="Locked" />}
          {p.row.reset_pending && <Chip size="small" color="info" variant="outlined" label="Reset" />}
          {!p.row.locked_out && !p.row.reset_pending && (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CoupleRow>) => (
        <Tooltip title="View couple">
          <IconButton size="small" onClick={() => openDetail(p.row.id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (forbidden) {
    return (
      <EmptyState
        icon="users"
        title="You do not have access to Wedding Books"
        description="This section needs the wedding_books resource. Ask a super admin to grant it."
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <PeopleIcon color="primary" />
        <Typography variant="h5">Couples</Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search name, email or wedding code"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <TextField
          select size="small" label="Verified" value={verified}
          onChange={(e) => setVerified(e.target.value as typeof verified)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="verified">Verified</MenuItem>
          <MenuItem value="unverified">Not verified</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Security" value={locked}
          onChange={(e) => setLocked(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="locked">Locked out</MenuItem>
          <MenuItem value="unlocked">Not locked</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Tier" value={tier}
          onChange={(e) => setTier(e.target.value as Tier | 'all')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All tiers</MenuItem>
          {TIERS.map((t) => <MenuItem key={t} value={t}>{TIER_META[t].label}</MenuItem>)}
        </TextField>
      </Stack>

      {error && !loading && rows.length === 0 ? (
        <EmptyState
          icon="error"
          title="Could not load couples"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          rowCount={total}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(p) => openDetail(String(p.id))}
          autoHeight
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}

      <CoupleDetailDialog
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        error={detailError}
        onClose={() => setDetailOpen(false)}
        onEdit={() => setEditOpen(true)}
        onResetPassword={() => setResetOpen(true)}
        onResendVerification={doResendVerification}
        onUnlock={doUnlock}
        onChangeTier={(b) => setTierBook(b)}
      />

      <CoupleEditDialog
        open={editOpen}
        couple={detail ? detail.couple : null}
        onClose={() => setEditOpen(false)}
        onSaved={(msg) => {
          setToast({ severity: 'success', message: msg });
          setEditOpen(false);
          refreshDetail();
          load();
        }}
        onError={(msg) => setToast({ severity: 'error', message: msg })}
      />

      <PasswordResetDialog
        open={resetOpen}
        email={detail ? detail.couple.email : ''}
        onClose={() => setResetOpen(false)}
        onConfirm={doReset}
      />

      <TierChangeDialog
        open={tierBook !== null}
        book={tierBook}
        tierSpec={detail ? detail.tier_spec : null}
        onClose={() => setTierBook(null)}
        onChanged={(r) => {
          setToast({
            severity: 'success',
            message: `${r.book.wedding_code} moved ${r.direction} to ${r.book.tier}. No charge was made. ${r.dashboard_cache_note}`,
          });
          setTierBook(null);
          refreshDetail();
          load();
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WeddingCouplesPage;
